import React, { useEffect } from 'react';
import SelectIcon from '../images/Icon20_Pointer.svg';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import {
  setActiveObject,
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';

import {
  addControlPointsToLine,
  disableSelectionControls,
  ensureArrowHead,
  overrideContainsPoint,
  removeControlPoints,
  resetContainsPoint,
  resetObjectStyle,
  selectObjectStyle,
  syncArrowHeadWithLine,
} from '../helpers/fabricHelpers';

import { __ } from '../../../core/utils';

const applyTextboxSelectionControls = (obj) => {
  if (obj?.objectType !== 'textbox') return;

  obj.editable = true;
  obj.selectable = true;
  obj.evented = true;
  obj.hasControls = true;
  obj.hasBorders = true;
  obj.lockRotation = true;
  obj.lockScalingX = false;
  obj.lockScalingY = true;
  obj.strokeUniform = true;

  obj.setControlsVisibility({
    tl: false,
    tr: false,
    bl: false,
    br: false,
    mt: false,
    mb: false,
    mtr: false,
    ml: true,
    mr: true,
  });
};

const applySelectBehaviorToObject = (obj, canvas, user_id) => {
  if (!obj) return;

  if (_.includes(['task', 'plan', 'specification'], obj.objectType)) {
    obj.hasControls = false;
    obj.selectable = true;
    obj.hasBorders = false;
    obj.evented = true;
    obj.lockMovementX = false;
    obj.lockMovementY = false;

    if (user_id != obj.userID) {
      obj.lockMovementX = true;
      obj.lockMovementY = true;
    }
  } else if (
    _.includes(
      ['line', 'freehand', 'polygon', 'cloud', 'arrow'],
      obj.objectType
    )
  ) {
    obj.hasControls = false;
    obj.selectable = true;
    obj.hasBorders = false;
    obj.evented = true;

    if (_.includes(['line', 'arrow'], obj.objectType)) {
      obj.perPixelTargetFind = false;
      obj.setCoords?.();

      if (obj.objectType === 'line') {
        overrideContainsPoint(obj, canvas);
      }
    }
  } else if (obj.objectType === 'arrow-head') {
    obj.selectable = false;
    obj.evented = false;
    obj.hasControls = false;
    obj.hasBorders = false;
  } else if (obj.objectType === 'textbox') {
    applyTextboxSelectionControls(obj);
  } else {
    obj.selectable = true;
    obj.evented = true;
    obj.hasControls = true;
    obj.hasBorders = true;
    obj.strokeUniform = true;
  }

  obj.setCoords?.();
};

const Select = ({
  canvas_editor: { activeTool, viewer, fabricCanvases },
  source,
  user_id,
}) => {
  const isActive = activeTool == 'select';

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'pointer';
      canvas.moveCursor = 'grabbing';

      canvas.selection = true;

      setCursorTooltip(__('canvas.tooltip.select_markups'));

      canvas.forEachObject((obj) => {
        applySelectBehaviorToObject(obj, canvas, user_id);
      });

      canvas.requestRenderAll();

      const activeObject = canvas.getActiveObject();
      if (activeObject?.type === 'activeSelection') {
        disableSelectionControls(activeObject);
        setActiveObject(activeObject.getObjects());
        setCursorTooltip(__('canvas.tooltip.delete_markups'));
        canvas.requestRenderAll();
      } else if (activeObject) {
        if (_.includes(['line', 'arrow'], activeObject.objectType)) {
          removeControlPoints(canvas);
          if (activeObject.objectType === 'line') {
            resetContainsPoint(activeObject);
          }
          addControlPointsToLine(activeObject, canvas, viewer, source);
        }

        selectObjectStyle(activeObject);
        setActiveObject(activeObject);
        setCursorTooltip(__('canvas.tooltip.delete_markups'));

        if (activeObject.objectType === 'textbox' && activeObject.editable) {
          activeObject.enterEditing?.();
          activeObject.hiddenTextarea?.focus();
        }

        canvas.requestRenderAll();
      }

      const handleObjectAdded = (e) => {
        const obj = e.target;

        if (!obj || _.includes(['controlpoint', 'highlight'], obj.objectType)) {
          return;
        }

        applySelectBehaviorToObject(obj, canvas, user_id);
      };

      const onPointerDown = (e) => {
        const target = canvas.findTarget(e);
        const activeObject = canvas.getActiveObject();

        if (!target) {
          if (!activeObject) {
            if (e.pointerType === 'mouse') {
              viewer?.setMouseNavEnabled(false);
            }
            return;
          }

          if (activeObject?.type === 'activeSelection') {
            activeObject.getObjects().forEach((obj) => {
              resetObjectStyle(obj);
            });
          } else if (activeObject) {
            resetObjectStyle(activeObject);
          }

          if (canvas.getActiveObject()) {
            canvas.discardActiveObject();
            canvas.requestRenderAll();
          }

          removeControlPoints(canvas);
          setActiveObject(null);
          viewer?.setMouseNavEnabled(true);
          updateTool({ tool: 'hand' });
          return;
        }

        if (e.pointerType === 'mouse') {
          viewer?.setMouseNavEnabled(false);
        }

        // if (e.pointerType === 'touch' && e.touches && e.touches.length != 2) {
        //   return viewer?.setMouseNavEnabled(false);
        // }

        // const target = canvas.findTarget(e);
        // if (target) {
        //   viewer?.setMouseNavEnabled(false);
        // } else {
        //   viewer?.setMouseNavEnabled(true);
        //   if (canvas.getActiveObject()) {
        //     canvas.discardActiveObject();
        //     canvas.requestRenderAll();
        //   }
        // }
      };

      const mouseUp = (e) => {
        viewer?.setMouseNavEnabled(true);

        const obj = e.target;

        if (_.includes(['line', 'arrow'], obj?.objectType)) {
          removeControlPoints(canvas);
          addControlPointsToLine(obj, canvas, viewer, source);
        }

        // if (_.includes(['task', 'plan'], obj?.objectType)) {
        //   canvas.discardActiveObject();

        //   canvas.requestRenderAll();
        // }
      };

      const mouseDown = (e) => {
        const obj = e.target;

        if (_.includes(['line', 'arrow'], obj?.objectType)) {
          removeControlPoints(canvas);
        }
      };

      const handleMouseDblClick = (e) => {
        const obj = e.target;

        if (obj?.objectType !== 'textbox' || !obj.editable) return;

        viewer?.setMouseNavEnabled(false);
        canvas.setActiveObject(obj);

        if (!obj.isEditing) {
          obj.enterEditing?.();
        }

        obj.hiddenTextarea?.focus();
        canvas.requestRenderAll();
      };

      const handleSelectionCreated = (e) => {
        const target = e.target;

        if (target.type != 'activeSelection') {
          const obj = e.selected?.[0];

          if (_.includes(['line', 'arrow'], obj?.objectType)) {
            removeControlPoints(canvas);
            if (obj.objectType === 'line') {
              resetContainsPoint(obj);
            }

            addControlPointsToLine(obj, canvas, viewer, source);
          }

          selectObjectStyle(obj);

          // if (!_.includes(['task', 'plan'], obj.objectType)) {
          //   setActiveObject(obj);
          //   setCursorTooltip(__('canvas.tooltip.delete_markups'));
          // }

          setActiveObject(obj);
          setCursorTooltip(__('canvas.tooltip.delete_markups'));
        } else {
          disableSelectionControls(target);

          setActiveObject(e.selected);

          setCursorTooltip(__('canvas.tooltip.delete_markups'));
        }
      };

      const handleSelectionUpdated = (e) => {
        const target = e.target;

        if (target.type != 'activeSelection') {
          const obj_selected = e.selected?.[0];
          const obj_deselected = e.deselected?.[0];

          if (_.includes(['line', 'arrow'], obj_selected?.objectType)) {
            removeControlPoints(canvas);
            if (obj_selected.objectType === 'line') {
              resetContainsPoint(obj_selected);
            }

            addControlPointsToLine(obj_selected, canvas, viewer, source);
          }

          if (obj_deselected?.objectType === 'line') {
            overrideContainsPoint(obj_deselected, canvas);
          }

          if (
            _.includes(
              ['line', 'controlpoint', 'arrow'],
              obj_deselected?.objectType
            ) &&
            !_.includes(
              ['line', 'controlpoint', 'arrow'],
              obj_selected?.objectType
            )
          ) {
            removeControlPoints(canvas);
          }

          selectObjectStyle(obj_selected);

          // if (_.includes(['task', 'plan'], obj_selected.objectType))
          //   setActiveObject(obj_selected);

          resetObjectStyle(obj_deselected);
        } else {
          target.getObjects().forEach((obj) => {
            resetObjectStyle(obj);
          });

          disableSelectionControls(target);

          setActiveObject(e.selected);
        }
      };

      const handleSelectionCleared = (e) => {
        // canvas.forEachObject((obj) => {
        //   if (_.includes(['task', 'plan'], obj.objectType)) {
        //     obj.selectable = true;
        //   }
        // });

        const obj = e.deselected?.[0];
        if (_.includes(['line', 'controlpoint', 'arrow'], obj?.objectType)) {
          removeControlPoints(canvas);
          if (obj?.objectType === 'line') {
            overrideContainsPoint(obj, canvas);
          }
        }
        resetObjectStyle(obj);

        setActiveObject(null);

        setCursorTooltip(__('canvas.tooltip.select_markups'));
      };

      const handleObjectModified = (e) => {
        const obj = e.target;
        const eventSource = e.source;

        if (!obj) return;

        // if (obj.type === 'activeSelection') {
        //   obj.forEachObject((child) => {
        //     child.setCoords();
        //   });
        //   return;
        // }

        if (!_.includes(['line', 'arrow'], obj?.objectType) || eventSource) {
          return;
        }

        const dx = obj.left - obj.startLeft;
        const dy = obj.top - obj.startTop;

        obj.set({
          x1: obj.x1 + dx,
          y1: obj.y1 + dy,
          x2: obj.x2 + dx,
          y2: obj.y2 + dy,
          startLeft: obj.left,
          startTop: obj.top,
        });

        obj.setCoords();
        if (obj.objectType === 'arrow') {
          ensureArrowHead(obj);
          syncArrowHeadWithLine(obj, { bringToFront: true });
        }

        addControlPointsToLine(obj, canvas, viewer, source);
      };

      const touchStart = (e) => {
        if (e.touches.length === 1) {
          viewer?.setMouseNavEnabled(false);
        } else if (e.touches.length === 2) {
          viewer?.setMouseNavEnabled(true);
        }
      };

      const touchEnd = (e) => {
        viewer?.setMouseNavEnabled(true);
      };

      canvas.on('selection:created', handleSelectionCreated);
      canvas.on('selection:updated', handleSelectionUpdated);
      canvas.on('mouse:down', mouseDown);
      canvas.on('mouse:dblclick', handleMouseDblClick);
      //canvas.on('mouse:move', mouseMove);

      canvas.on('mouse:up', mouseUp);
      canvas.on('object:added', handleObjectAdded);
      canvas.on('selection:cleared', handleSelectionCleared);
      canvas.on('object:modified', handleObjectModified);

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

      canvas.upperCanvasEl.addEventListener('touchstart', touchStart);
      canvas.upperCanvasEl.addEventListener('touchend', touchEnd);

      disposers.push(() => {
        if (canvas?.upperCanvasEl) {
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );
          canvas.upperCanvasEl.removeEventListener('touchstart', touchStart);
          canvas.upperCanvasEl.removeEventListener('touchend', touchEnd);

          if (canvas?.getActiveObject()) {
            canvas.discardActiveObject();
            canvas.requestRenderAll();
          }

          canvas.selection = false;

          removeControlPoints(canvas);
        }

        canvas.off('selection:created', handleSelectionCreated);
        canvas.off('selection:cleared', handleSelectionCleared);
        canvas.off('selection:updated', handleSelectionUpdated);
        canvas.off('mouse:down', mouseDown);
        canvas.off('mouse:dblclick', handleMouseDblClick);
        //canvas.off('mouse:move', mouseMove);
        canvas.off('mouse:up', mouseUp);
        canvas.off('object:added', handleObjectAdded);
        canvas.off('object:modified', handleObjectModified);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };

    return () => {
      if (canvas.getActiveObject()) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }

      canvas.selection = false;

      canvas.off('selection:created', handleSelectionCreated);
      canvas.off('selection:cleared', handleSelectionCleared);
      canvas.off('selection:updated', handleSelectionUpdated);
      canvas.off('mouse:down', mouseDown);
      //canvas.off('mouse:move', mouseMove);
      canvas.off('mouse:up', mouseUp);
      canvas.off('object:modified', handleObjectModified);
      canvas.upperCanvasEl.removeEventListener('pointerdown', onPointerDown);
      canvas.upperCanvasEl.removeEventListener('touchstart', touchStart);
      canvas.upperCanvasEl.removeEventListener('touchend', touchEnd);
      removeControlPoints(canvas);
    };
  }, [isActive, fabricCanvases, viewer, source]);

  const handleClick = () => {
    updateTool({ tool: 'select' });
  };

  return (
    <Button
      id='select'
      label={__('canvas.toolbar.select')}
      icon={SelectIcon}
      active={isActive}
      onClick={handleClick}
      extraClass={['withSpacer']}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    canvas_editor: state.canvas_editor,
    user_id: state.auth.id,
  };
};

export default connect(mapStateToProps, null)(Select);
